import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Button,
  TextInput,
  Textarea,
  Alert,
  Badge,
  Checkbox,
  Spinner,
  Modal,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow
} from 'flowbite-react';
import {
  HiPlus,
  HiPencil,
  HiTrash,
  HiUserGroup,
  HiKey,
  HiExclamation,
  HiRefresh
} from 'react-icons/hi';
import { rbacApi, type Role, type Permission } from '../api/rbac';
import { useAuth } from '../contexts/AuthContext';

interface RoleFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  role?: Role | null;
  permissions: Permission[];
}

const RoleFormModal: React.FC<RoleFormModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  role,
  permissions
}) => {
  const [formData, setFormData] = useState({
    roleName: '',
    roleDescription: '',
    isActive: true,
    permissions: [] as number[]
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (role) {
      setFormData({
        roleName: role.roleName,
        roleDescription: role.roleDescription,
        isActive: role.isActive,
        permissions: role.permissions?.map(p => p.permissionId) ?? []
      });
    } else {
      setFormData({
        roleName: '',
        roleDescription: '',
        isActive: true,
        permissions: []
      });
    }
  }, [role]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.roleName || !formData.roleDescription) {
      setError('Role name and description are required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (role) {
        // For system roles, only send permissions (not name, description, or isActive)
        const updateData = isSystemRole
          ? { permissions: formData.permissions }
          : formData;

        await rbacApi.updateRole(role.roleId, updateData);
      } else {
        await rbacApi.createRole(formData);
      }

      onSuccess();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save role');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      roleName: '',
      roleDescription: '',
      isActive: true,
      permissions: []
    });
    setError(null);
    onClose();
  };

  const togglePermission = (permissionId: number) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter(id => id !== permissionId)
        : [...prev.permissions, permissionId]
    }));
  };

  // Group permissions by category
  const groupedPermissions = permissions.reduce((acc, perm) => {
    const parts = perm.permissionName.split('_');
    const category = parts.length > 1 ? parts[1] : 'other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(perm);
    return acc;
  }, {} as Record<string, Permission[]>);

  // Check if this is a system role
  const isSystemRole = !!(role && ['superAdmin', 'organizationAdmin', 'viewer'].includes(role.roleName));

  return (
    <Modal show={isOpen} onClose={handleClose} dismissible popup size="2xl">
      <div className="p-6 flex flex-col max-h-[90vh]">
        <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-4">
          {role ? 'Edit Role' : 'Create New Role'}
        </h3>

        {error && (
          <Alert color="failure" onDismiss={() => setError(null)} className="mb-4">
            {error}
          </Alert>
        )}

        {isSystemRole && (
          <Alert color="warning" icon={HiExclamation} className="mb-4">
            This is a system role. You can only modify permissions, not the name or description.
          </Alert>
        )}

        {/* Scrollable form content */}
        <div className="flex-1 overflow-y-auto min-h-0">
          <form onSubmit={handleSubmit} className="space-y-4" id="role-form">
          {/* Role Name */}
          <div>
            <Label htmlFor="roleName" className="mb-2">
              Role Name
            </Label>
            <TextInput
              id="roleName"
              placeholder="e.g., Data Analyst"
              value={formData.roleName}
              onChange={(e) => setFormData({ ...formData, roleName: e.target.value })}
              disabled={isSystemRole}
              required
            />
          </div>

          {/* Role Description */}
          <div>
            <Label htmlFor="roleDescription" className="mb-2">
              Description
            </Label>
            <Textarea
              id="roleDescription"
              placeholder="Describe the purpose and responsibilities of this role..."
              value={formData.roleDescription}
              onChange={(e) => setFormData({ ...formData, roleDescription: e.target.value })}
              rows={3}
              disabled={isSystemRole}
              required
            />
          </div>

          {/* Active Status */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              disabled={isSystemRole}
            />
            <Label htmlFor="isActive">
              Active (users can be assigned this role)
            </Label>
          </div>

          {/* Permissions */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <Label className="block">
                Permissions
              </Label>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {formData.permissions.length} of {permissions.length} selected
                </span>
                <Button
                  type="button"
                  size="xs"
                  color="gray"
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    permissions: permissions.map(p => p.permissionId)
                  }))}
                >
                  Select All
                </Button>
                <Button
                  type="button"
                  size="xs"
                  color="gray"
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    permissions: []
                  }))}
                >
                  Clear All
                </Button>
              </div>
            </div>
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 max-h-80 overflow-y-auto">
              {Object.entries(groupedPermissions).map(([category, perms]) => (
                <div key={category} className="mb-4 last:mb-0">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-gray-700 dark:text-gray-300 capitalize">
                      {category.replace(/_/g, ' ')}
                    </h4>
                    <div className="flex space-x-1">
                      <Button
                        type="button"
                        size="xs"
                        color="light"
                        onClick={() => {
                          const categoryPermIds = perms.map(p => p.permissionId);
                          setFormData(prev => ({
                            ...prev,
                            permissions: [
                              ...new Set([...prev.permissions, ...categoryPermIds])
                            ]
                          }));
                        }}
                      >
                        All
                      </Button>
                      <Button
                        type="button"
                        size="xs"
                        color="light"
                        onClick={() => {
                          const categoryPermIds = perms.map(p => p.permissionId);
                          setFormData(prev => ({
                            ...prev,
                            permissions: prev.permissions.filter(id => !categoryPermIds.includes(id))
                          }));
                        }}
                      >
                        None
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2 ml-4">
                    {perms.map(perm => (
                      <div key={perm.permissionId} className="flex items-start hover:bg-gray-50 dark:hover:bg-gray-700 p-1 rounded">
                        <Checkbox
                          id={`perm-${perm.permissionId}`}
                          checked={formData.permissions.includes(perm.permissionId)}
                          onChange={() => togglePermission(perm.permissionId)}
                          className="mt-0.5"
                        />
                        <Label
                          htmlFor={`perm-${perm.permissionId}`}
                          className="ml-2 cursor-pointer flex-1"
                        >
                          <div>
                            <div className="font-medium text-sm text-gray-900 dark:text-white">{perm.permissionName}</div>
                            {perm.permissionDescription && (
                              <div className="text-xs text-gray-500 dark:text-gray-400">{perm.permissionDescription}</div>
                            )}
                          </div>
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          </form>
        </div>

        {/* Actions - Fixed footer with border */}
        <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 dark:border-gray-700 mt-4">
          <Button color="gray" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" form="role-form" disabled={loading}>
            {loading ? <Spinner size="sm" /> : (role ? 'Update Role' : 'Create Role')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

interface RoleUsersModalProps {
  isOpen: boolean;
  onClose: () => void;
  role: Role | null;
}

const RoleUsersModal: React.FC<RoleUsersModalProps> = ({ isOpen, onClose, role }) => {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);

  useEffect(() => {
    if (isOpen && role) {
      loadUsers();
    }
  }, [isOpen, role, page]);

  const loadUsers = async () => {
    if (!role) return;

    try {
      setLoading(true);
      const result = await rbacApi.getRoleUsers(role.roleId, {
        page,
        pageSize: 10
      });
      setUsers(result.users);
      setTotalUsers(result.totalCount);
    } catch (err) {
      console.error('Failed to load role users:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!role) return null;

  return (
    <Modal show={isOpen} onClose={onClose} dismissible popup size="lg">
      <div className="p-6">
        <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-4">
          Users with Role: {role.roleName}
        </h3>

        <div className="mb-4">
          <Badge color="gray">Total Users: {totalUsers}</Badge>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <Spinner size="lg" />
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            No users assigned to this role
          </div>
        ) : (
          <div className="space-y-2">
            {users.map((user) => (
              <div key={user.userId} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">{user.personName}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {user.isActive ? (
                        <Badge color="success" size="xs">Active</Badge>
                      ) : (
                        <Badge color="failure" size="xs">Inactive</Badge>
                      )}
                      {user.lastLoginAt && (
                        <span className="ml-2 text-xs">
                          Last login: {new Date(user.lastLoginAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Pagination */}
            {totalUsers > 10 && (
              <div className="flex justify-center space-x-2 mt-4">
                <Button
                  size="xs"
                  color="gray"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <span className="px-3 py-1 text-sm">
                  Page {page} of {Math.ceil(totalUsers / 10)}
                </span>
                <Button
                  size="xs"
                  color="gray"
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= Math.ceil(totalUsers / 10)}
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end mt-6">
          <Button color="gray" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export const RoleManagementPage: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Modal states
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);

  // Filter states
  const [includeInactive, setIncludeInactive] = useState(false);

  const { currentUser } = useAuth();

  // Load roles and permissions
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [rolesData, permsData] = await Promise.all([
        rbacApi.listRoles({
          includeInactive,
          includePermissions: true
        }),
        rbacApi.listPermissions()
      ]);

      // Handle both array response and object response formats
      setRoles(Array.isArray(rolesData) ? rolesData : (rolesData.roles || []));
      setPermissions(Array.isArray(permsData) ? permsData : (permsData.permissions || []));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle create role
  const handleCreateRole = () => {
    setSelectedRole(null);
    setShowRoleModal(true);
  };

  // Handle edit role
  const handleEditRole = (role: Role) => {
    setSelectedRole(role);
    setShowRoleModal(true);
  };

  // Handle delete role
  const handleDeleteRole = async (role: Role) => {
    // Prevent deletion of system roles
    if (['superAdmin', 'organizationAdmin', 'viewer'].includes(role.roleName)) {
      setError('Cannot delete system roles');
      return;
    }

    if ((role.userCount ?? 0) > 0) {
      setError(`Cannot delete role with ${role.userCount ?? 0} assigned users`);
      return;
    }

    if (!confirm(`Are you sure you want to delete the role "${role.roleName}"?`)) {
      return;
    }

    try {
      await rbacApi.deleteRole(role.roleId);
      setSuccessMessage('Role deleted successfully');
      await loadData();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete role');
    }
  };

  // Handle view users
  const handleViewUsers = (role: Role) => {
    setSelectedRole(role);
    setShowUsersModal(true);
  };

  // Check if user can manage roles (only superAdmin)
  const canManageRoles = currentUser?.role === 'superAdmin';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Role Management</h1>
          <p className="text-gray-600 dark:text-gray-400">Configure roles and their permissions</p>
        </div>

        {/* Alerts */}
        {error && (
          <Alert color="failure" onDismiss={() => setError(null)} className="mb-4">
            {error}
          </Alert>
        )}
        {successMessage && (
          <Alert color="success" onDismiss={() => setSuccessMessage(null)} className="mb-4">
            {successMessage}
          </Alert>
        )}

        {/* Actions Bar */}
        <Card className="mb-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="includeInactive"
                  checked={includeInactive}
                  onChange={(e) => setIncludeInactive(e.target.checked)}
                />
                <Label htmlFor="includeInactive">
                  Show inactive roles
                </Label>
              </div>
            </div>

            <div className="flex space-x-2">
              <Button size="sm" color="gray" onClick={loadData}>
                <HiRefresh className="mr-2 h-4 w-4" />
                Refresh
              </Button>

              {canManageRoles && (
                <Button size="sm" onClick={handleCreateRole}>
                  <HiPlus className="mr-2 h-4 w-4" />
                  Create Role
                </Button>
              )}
            </div>
          </div>
        </Card>

        {/* Roles Table */}
        <Card>
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <Spinner size="xl" />
            </div>
          ) : roles.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              No roles found. Try adjusting your filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table hoverable>
                <TableHead>
                  <TableHeadCell>Role Name</TableHeadCell>
                  <TableHeadCell>Description</TableHeadCell>
                  <TableHeadCell>Status</TableHeadCell>
                  <TableHeadCell>Users</TableHeadCell>
                  <TableHeadCell>Permissions</TableHeadCell>
                  <TableHeadCell>Actions</TableHeadCell>
                </TableHead>
                <TableBody className="divide-y">
                  {roles.map((role) => {
                    const isSystemRole = ['superAdmin', 'organizationAdmin', 'viewer'].includes(role.roleName);

                    return (
                      <TableRow key={role.roleId} className="bg-white dark:bg-gray-800">
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <div>
                              <div className="font-medium text-gray-900 dark:text-white">
                                {role.roleName}
                              </div>
                              {isSystemRole && (
                                <Badge color="purple" size="xs">System</Badge>
                              )}
                            </div>
                          </div>
                        </TableCell>

                        <TableCell>
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            {role.roleDescription}
                          </div>
                        </TableCell>

                        <TableCell>
                          {role.isActive ? (
                            <Badge color="success">Active</Badge>
                          ) : (
                            <Badge color="failure">Inactive</Badge>
                          )}
                        </TableCell>

                        <TableCell>
                          <div className="flex items-center text-gray-700 dark:text-gray-300">
                            <HiUserGroup className="mr-2 h-4 w-4 text-gray-400 dark:text-gray-500" />
                            <span>{role.userCount ?? 0}</span>
                          </div>
                        </TableCell>

                        <TableCell>
                          <div>
                            <div className="flex items-center mb-1">
                              <HiKey className="mr-2 h-4 w-4 text-gray-400 dark:text-gray-500" />
                              <span className="text-sm text-gray-700 dark:text-gray-300">{role.permissions?.length ?? 0} total</span>
                            </div>
                            <div className="flex flex-wrap gap-1 max-w-xs">
                              {role.permissions?.slice(0, 3).map((perm) => (
                                <Badge key={perm.permissionId} color="gray" size="xs">
                                  {perm.permissionName}
                                </Badge>
                              ))}
                              {(role.permissions?.length ?? 0) > 3 && (
                                <Badge color="info" size="xs">
                                  +{(role.permissions?.length ?? 0) - 3} more
                                </Badge>
                              )}
                            </div>
                          </div>
                        </TableCell>

                        <TableCell>
                          <div className="flex space-x-2">
                            <Button
                              size="xs"
                              color="gray"
                              onClick={() => handleViewUsers(role)}
                              title="View Users"
                              aria-label="View Users"
                            >
                              <HiUserGroup className="h-3 w-3" />
                            </Button>

                            {canManageRoles && (
                              <>
                                <button
                                  onClick={() => handleEditRole(role)}
                                  title="Edit Role"
                                  aria-label="Edit Role"
                                  className="inline-flex items-center justify-center px-2 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
                                >
                                  <HiPencil className="h-3 w-3" />
                                </button>

                                {!isSystemRole && (
                                  <Button
                                    size="xs"
                                    color="failure"
                                    onClick={() => handleDeleteRole(role)}
                                    disabled={(role.userCount ?? 0) > 0}
                                    title={(role.userCount ?? 0) > 0 ? 'Cannot delete role with active users' : 'Delete Role'}
                                    aria-label={(role.userCount ?? 0) > 0 ? 'Cannot delete role with active users' : 'Delete Role'}
                                  >
                                    <HiTrash className="h-3 w-3" />
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>

        {/* Modals */}
        <RoleFormModal
          isOpen={showRoleModal}
          onClose={() => {
            setShowRoleModal(false);
            setSelectedRole(null);
          }}
          onSuccess={() => {
            setSuccessMessage(selectedRole ? 'Role updated successfully' : 'Role created successfully');
            loadData();
            setTimeout(() => setSuccessMessage(null), 3000);
          }}
          role={selectedRole}
          permissions={permissions}
        />

        <RoleUsersModal
          isOpen={showUsersModal}
          onClose={() => {
            setShowUsersModal(false);
            setSelectedRole(null);
          }}
          role={selectedRole}
        />
      </div>
    </div>
  );
};